if (!customElements.get('product-modal')) {
  class ProductModal extends HTMLElement {
    constructor() {
      super();
      this._index = 0;
      this._isOpen = false;
      this._touchStartX = 0;
      this._touchStartY = 0;
      this._touchDeltaX = 0;
      this._touchActive = false;
      this._touchAxisLocked = null; // 'x' | 'y' | null

      this._onKeydown = this._onKeydown.bind(this);
      this._onDialogClick = this._onDialogClick.bind(this);
      this._onDialogClose = this._onDialogClose.bind(this);
      this._onTouchStart = this._onTouchStart.bind(this);
      this._onTouchMove = this._onTouchMove.bind(this);
      this._onTouchEnd = this._onTouchEnd.bind(this);
    }

    connectedCallback() {
      // Guard re-entry: document.body.appendChild() below disconnects then
      // reconnects this element, which fires connectedCallback again. Without
      // this guard every listener (controls, dialog click/close, swipe) gets
      // wired twice and prev/next advance by 2 per click.
      if (this._initialized) return;
      this._initialized = true;

      // Move to body so position:fixed/dialog work outside any transformed ancestor.
      document.body.appendChild(this);

      this.dialog = this.querySelector('.brand-lightbox__dialog');
      this.track = this.querySelector('[data-brand-lightbox-track]');
      this.viewport = this.querySelector('[data-brand-lightbox-viewport]');
      this.counterEl = this.querySelector('[data-brand-lightbox-counter-current]');

      this._wireControls();

      if (this.dialog) {
        this.dialog.addEventListener('click', this._onDialogClick);
        this.dialog.addEventListener('close', this._onDialogClose);
      }
    }

    get _slideCount() {
      return this.querySelectorAll('.brand-lightbox__slide').length;
    }

    _wireControls() {
      this.querySelector('[data-brand-lightbox-prev]')?.addEventListener('click', (e) => {
        e.stopPropagation();
        this.prev();
      });
      this.querySelector('[data-brand-lightbox-next]')?.addEventListener('click', (e) => {
        e.stopPropagation();
        this.next();
      });
      this.querySelector('[data-brand-lightbox-close]')?.addEventListener('click', (e) => {
        e.stopPropagation();
        this.hide();
      });

      // Stop clicks inside the controls/counter from being treated as backdrop clicks.
      this.querySelector('.brand-lightbox__chrome')?.addEventListener('click', (e) => {
        e.stopPropagation();
      });

      // Swipe handlers (passive for perf where possible).
      if (this.track) {
        this.track.addEventListener('touchstart', this._onTouchStart, { passive: true });
        this.track.addEventListener('touchmove', this._onTouchMove, { passive: false });
        this.track.addEventListener('touchend', this._onTouchEnd, { passive: true });
        this.track.addEventListener('touchcancel', this._onTouchEnd, { passive: true });
      }
    }

    // Promote every slide image to eager so prev/next swaps don't wait for a
    // cold fetch + decode. Idempotent via data-warmed so re-opens don't churn.
    _warmAllSlides() {
      const imgs = this.querySelectorAll('.brand-lightbox__slide img');
      imgs.forEach((img, i) => {
        if (img.dataset.lightboxWarmed === '1') return;
        img.loading = 'eager';
        if (i === this._index) img.fetchPriority = 'high';
        img.dataset.lightboxWarmed = '1';
      });
    }

    show(opener) {
      this.openedBy = opener;
      this._warmAllSlides();

      // Find the index of the clicked thumbnail by media-id suffix match.
      const openerMediaId = opener?.getAttribute?.('data-media-id') || '';
      let startIndex = 0;
      if (openerMediaId) {
        // Slides have data-media-id="{section.id}-{media.id}"; opener has just media.id.
        const match = this.querySelector(
          `.brand-lightbox__slide[data-media-id$="-${openerMediaId}"]`
        );
        if (match) {
          startIndex = parseInt(match.getAttribute('data-slide-index'), 10) || 0;
        }
      }
      this._index = Math.max(0, Math.min(startIndex, this._slideCount - 1));

      // View Transitions: tag the source thumbnail + the destination slide image
      // with a shared name, run startViewTransition, clear afterwards.
      const sourceImg = opener?.closest?.('.product-media-container')?.querySelector?.('img');
      const targetSlide = this.querySelectorAll('.brand-lightbox__slide')[this._index];
      const targetImg = targetSlide?.querySelector?.('img');

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const supportsVT = typeof document.startViewTransition === 'function';

      const doOpen = () => {
        document.body.classList.add('overflow-hidden');
        if (typeof this.dialog.showModal === 'function') {
          this.dialog.showModal();
        } else {
          this.dialog.setAttribute('open', '');
          this.dialog.classList.add('brand-lightbox__dialog--polyfill-open');
        }
        this._setIndex(this._index, /*instant*/ true);
        this._isOpen = true;
        document.addEventListener('keydown', this._onKeydown);
        if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
        if (typeof trapFocus === 'function') {
          trapFocus(this, this.querySelector('[data-brand-lightbox-close]') || this.dialog);
        }
      };

      if (supportsVT && !reduceMotion && sourceImg && targetImg) {
        sourceImg.style.viewTransitionName = 'brand-lightbox-hero';
        targetImg.style.viewTransitionName = 'brand-lightbox-hero';
        const cleanup = () => {
          if (sourceImg) sourceImg.style.viewTransitionName = '';
          if (targetImg) targetImg.style.viewTransitionName = '';
        };
        // Skip any in-flight transition before starting a new one so the
        // previous run's `ready`/`finished` promises reject in a controlled
        // way instead of surfacing as unhandled InvalidStateError exceptions.
        if (this._activeTransition && typeof this._activeTransition.skipTransition === 'function') {
          try { this._activeTransition.skipTransition(); } catch (_) {}
        }
        const transition = document.startViewTransition(() => {
          doOpen();
        });
        this._activeTransition = transition;
        // Attach .catch handlers to every promise on the ViewTransition so
        // an aborted transition does not surface as an unhandled rejection.
        transition.ready.catch(() => {});
        transition.updateCallbackDone.catch(() => {});
        transition.finished.then(cleanup, cleanup);
      } else {
        doOpen();
      }
    }

    hide() {
      if (!this._isOpen) return;
      this._isOpen = false;
      document.removeEventListener('keydown', this._onKeydown);
      document.body.classList.remove('overflow-hidden');
      document.body.dispatchEvent(new CustomEvent('modalClosed'));
      try {
        if (typeof this.dialog.close === 'function') {
          this.dialog.close();
        } else {
          this.dialog.removeAttribute('open');
        }
        this.dialog.classList.remove('brand-lightbox__dialog--polyfill-open');
      } catch (e) {
        /* dialog already closed */
      }
      if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.openedBy);
    }

    /* ----- Carousel ----- */
    next() {
      if (this._slideCount < 2) return;
      this._setIndex((this._index + 1) % this._slideCount);
    }

    prev() {
      if (this._slideCount < 2) return;
      this._setIndex((this._index - 1 + this._slideCount) % this._slideCount);
    }

    _setIndex(i, instant = false) {
      this._index = i;
      if (instant) this.track.style.transition = 'none';
      this.track.style.transform = `translate3d(${-i * 100}%, 0, 0)`;
      if (instant) {
        // Force reflow so the next transition takes effect.
        // eslint-disable-next-line no-unused-expressions
        this.track.offsetHeight;
        this.track.style.transition = '';
      }
      if (this.counterEl) this.counterEl.textContent = String(i + 1);
    }

    /* ----- Keyboard ----- */
    _onKeydown(event) {
      if (!this._isOpen) return;
      const code = event.code;
      if (code === 'Escape') {
        event.preventDefault();
        this.hide();
      } else if (code === 'ArrowRight') {
        event.preventDefault();
        this.next();
      } else if (code === 'ArrowLeft') {
        event.preventDefault();
        this.prev();
      }
    }

    /* ----- Backdrop click ----- */
    _onDialogClick(event) {
      // Native <dialog> click target === dialog itself when user clicks the backdrop.
      if (event.target === this.dialog) {
        this.hide();
      }
    }

    _onDialogClose() {
      // Triggered by Esc-on-dialog (browser default) or programmatic close.
      // Defensive cleanup if hide() didn't run.
      if (this._isOpen) {
        this._isOpen = false;
        document.body.classList.remove('overflow-hidden');
        document.removeEventListener('keydown', this._onKeydown);
        if (typeof removeTrapFocus === 'function') removeTrapFocus(this.openedBy);
      }
    }

    /* ----- Swipe ----- */
    _onTouchStart(event) {
      if (!this._isOpen || this._slideCount < 2) return;
      const t = event.touches[0];
      this._touchStartX = t.clientX;
      this._touchStartY = t.clientY;
      this._touchDeltaX = 0;
      this._touchActive = true;
      this._touchAxisLocked = null;
      // Cache once per swipe — touchmove fires 60+/s and clientWidth forces layout.
      this._viewportWidth = this.viewport.clientWidth;
    }

    _onTouchMove(event) {
      if (!this._touchActive) return;
      const t = event.touches[0];
      const dx = t.clientX - this._touchStartX;
      const dy = t.clientY - this._touchStartY;

      if (this._touchAxisLocked === null) {
        if (Math.abs(dx) > 8 || Math.abs(dy) > 8) {
          this._touchAxisLocked = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        } else {
          return;
        }
      }
      if (this._touchAxisLocked !== 'x') return;

      event.preventDefault(); // prevent vertical scroll while swiping horizontally
      this._touchDeltaX = dx;
      this.track.classList.add('is-dragging');
      // Resistance at edges
      let offset = dx;
      if ((this._index === 0 && dx > 0) || (this._index === this._slideCount - 1 && dx < 0)) {
        offset = dx * 0.3;
      }
      const base = -this._index * this._viewportWidth;
      this.track.style.transform = `translate3d(${base + offset}px, 0, 0)`;
    }

    _onTouchEnd() {
      if (!this._touchActive) return;
      this._touchActive = false;
      this.track.classList.remove('is-dragging');

      const threshold = 40;
      const dx = this._touchDeltaX;
      this._touchDeltaX = 0;

      if (this._touchAxisLocked !== 'x') {
        // Vertical / no-swipe — restore current slide.
        this._setIndex(this._index);
        return;
      }

      if (dx <= -threshold && this._index < this._slideCount - 1) {
        this._setIndex(this._index + 1);
      } else if (dx >= threshold && this._index > 0) {
        this._setIndex(this._index - 1);
      } else {
        this._setIndex(this._index); // snap back
      }
    }
  }

  customElements.define('product-modal', ProductModal);
}
